import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable } from '../../components/shared/DataTable';
import { Modal } from '../../components/shared/Modal';
import { useToast } from '../../components/shared/Toast';
import {
  MdAdd,
  MdBusiness,
  MdFilterList,
  MdSearch,
  MdDelete,
  MdEdit,
  MdAssignment,
} from 'react-icons/md';
import { useDebouncedSearch } from '@euroasiann/shared';
import { ClearFiltersButton, PageSkeleton } from '@euroasiann/shared/ui';
import { cn } from '../../lib/utils';
import { useCurrency } from '../../context/CurrencyContext';
import { authenticatedFetch } from '../../lib/api';
import { CurrencyAmountInput } from '../../components/shared/CurrencyAmountInput';
import { CountryCodeSelect } from '../../components/ui/CountryCodeSelect';
import { loadStoredCurrency } from '../../lib/currency';
import {
  DEFAULT_CONTACT_PHONE_COUNTRY_CODE,
  formatContactPhone,
  normalizeContactPhoneLocal,
  splitContactPhone,
} from '../../utils/contact-phone';
import { OrganizationInvitationsModal } from './OrganizationInvitationsModal';
import { OnboardingDataPage } from '../Onboarding/OnboardingDataPage';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Organization {
  _id: string;
  name: string;
  type: string;
  portalType: string;
  isActive: boolean;
  licenseKey?: string;
  createdAt?: string;
  vendorCategory?: 'global' | 'contracted';
  vendorCategoryLabel?: string;
  invitedByCustomerName?: string | null;
}

export function OrganizationsPage() {
  const { currency: portalCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') === 'onboarding' ? 'onboarding' : 'organizations';

  const handleTabChange = (tab: 'organizations' | 'onboarding') => {
    if (tab === 'organizations') {
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    } else {
      searchParams.set('tab', 'onboarding');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const [filterActive, setFilterActive] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [vendorTypeFilter, setVendorTypeFilter] = useState<'all' | 'global' | 'contracted'>('all');
  const {
    inputValue: searchQuery,
    submittedValue: submittedSearch,
    setInputValue: setSearchQuery,
    clear: clearSearch,
    onKeyDown: onSearchKeyDown,
  } = useDebouncedSearch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [invitationsOrg, setInvitationsOrg] = useState<Organization | null>(null);

  // Editing state
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);

  // Form fields
  const [orgName, setOrgName] = useState('');
  const [orgEmail, setOrgEmail] = useState('');
  const [orgPhoneCountryCode, setOrgPhoneCountryCode] = useState(DEFAULT_CONTACT_PHONE_COUNTRY_CODE);
  const [orgPhone, setOrgPhone] = useState('');
  const [adminName, setAdminName] = useState('');
  const [numberOfVessels, setNumberOfVessels] = useState('');
  const [orgType, setOrgType] = useState<'customer' | 'vendor' | 'internal' | 'tech'>('customer');
  const [chargePerVessel, setChargePerVessel] = useState<string>('500');
  const [chargePerVesselCurrency, setChargePerVesselCurrency] = useState(loadStoredCurrency);
  const [formError, setFormError] = useState<string | null>(null);
  const [editFormLoading, setEditFormLoading] = useState(false);

  // Fetch organizations
  const { data: orgsData = [], isLoading } = useQuery<Organization[]>({
    queryKey: ['organizations', filterActive, filterType, vendorTypeFilter, submittedSearch],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      const effectiveType =
        vendorTypeFilter !== 'all' ? 'vendor' : filterType !== 'all' ? filterType : undefined;
      if (effectiveType) params.append('type', effectiveType);
      if (filterActive !== 'all') params.append('isActive', filterActive === 'active' ? 'true' : 'false');
      if (vendorTypeFilter !== 'all') params.append('vendorScope', vendorTypeFilter);
      if (submittedSearch) params.append('search', submittedSearch);

      const response = await fetch(`${API_URL}/api/v1/admin/organizations?${params}`, {
        signal,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to fetch' }));
        throw new Error(error.error || 'Failed');
      }

      const json = await response.json();
      // Handle both paginated {data: {data:[]}} and non-paginated {data:[]} shapes
      const raw = json.data?.data ?? json.data;
      return (Array.isArray(raw) ? raw : []) as Organization[];
    },
  });

  const filteredOrgs = React.useMemo(() => {
    if (!searchQuery) return orgsData;
    const query = searchQuery.toLowerCase();
    return orgsData.filter(
      (org) =>
        org.name.toLowerCase().includes(query) ||
        org.type.toLowerCase().includes(query)
    );
  }, [orgsData, searchQuery]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (orgId: string) => {
      const response = await fetch(`${API_URL}/api/v1/admin/organizations/${orgId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete organization');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      showToast('Organization deleted successfully!', 'success');
    },
    onError: (error: Error) => {
      showToast(`Failed to delete: ${error.message}`, 'error');
    },
  });

  const handleDelete = (org: Organization) => {
    if (window.confirm(`Are you sure you want to delete ${org.name}?`)) {
      deleteMutation.mutate(org._id);
    }
  };

  const resetCreateForm = () => {
    setOrgName('');
    setOrgEmail('');
    setOrgPhoneCountryCode(DEFAULT_CONTACT_PHONE_COUNTRY_CODE);
    setOrgPhone('');
    setAdminName('');
    setNumberOfVessels('');
    setOrgType('customer');
    setChargePerVessel('500');
    setChargePerVesselCurrency(loadStoredCurrency());
    setFormError(null);
  };

  const composedOrgPhone = () => formatContactPhone(orgPhoneCountryCode, orgPhone) || undefined;

  // Save (Create via invite email + Update)
  const saveMutation = useMutation({
    mutationFn: async () => {
      const trimmedName = orgName.trim();
      if (!trimmedName) throw new Error('Organization name is required');

      if (editingOrg) {
        const contactParts = adminName.trim().split(/\s+/);
        const firstName = contactParts[0] || 'Organization';
        const lastName = contactParts.slice(1).join(' ') || 'Admin';

        const response = await authenticatedFetch(`/api/v1/admin/organizations/${editingOrg._id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: trimmedName,
            type: orgType,
            portalType: orgType,
            chargePerVessel: orgType === 'customer' ? Number(chargePerVessel) || 0 : undefined,
            chargePerVesselCurrency:
              orgType === 'customer' ? chargePerVesselCurrency : undefined,
            numberOfVessels: numberOfVessels ? Number(numberOfVessels) : undefined,
            phone: composedOrgPhone(),
            primaryAdmin: {
              firstName,
              lastName,
              phone: composedOrgPhone(),
            },
          }),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || err.message || 'Update failed');
        }
        const result = await response.json();
        return { ...result, isEdit: true };
      }

      const trimmedEmail = orgEmail.trim();
      if (!trimmedEmail) throw new Error('Admin email is required');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        throw new Error('Please enter a valid email address');
      }
      if (!adminName.trim()) throw new Error('Admin name is required');

      const contactParts = adminName.trim().split(/\s+/);
      const firstName = contactParts[0] || 'Organization';
      const lastName = contactParts.slice(1).join(' ') || 'Admin';

      if (orgType === 'customer') {
        if (!chargePerVessel.trim() || Number(chargePerVessel) <= 0) {
          throw new Error('Per vessel price is required for customers');
        }
      }

      if (orgType === 'customer' || orgType === 'vendor') {
        const payload = {
          organizationName: trimmedName,
          organizationType: orgType,
          adminEmail: trimmedEmail.toLowerCase(),
          firstName,
          lastName,
          phone: composedOrgPhone(),
          numberOfVessels: numberOfVessels ? Number(numberOfVessels) : undefined,
          chargePerVessel: orgType === 'customer' ? Number(chargePerVessel) || 0 : undefined,
          chargePerVesselCurrency:
            orgType === 'customer' ? chargePerVesselCurrency : undefined,
          isActive: true,
        };

        const response = await authenticatedFetch('/api/v1/admin/organizations/invite', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.success) {
          throw new Error(data?.error || data?.message || 'Failed to invite organization');
        }
        return data;
      }

      const response = await fetch(`${API_URL}/api/v1/admin/organizations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          name: trimmedName,
          type: orgType,
          portalType: orgType,
          adminEmail: trimmedEmail,
          firstName,
          lastName,
          phone: composedOrgPhone(),
          isActive: true,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || err.message || 'Operation failed');
      }
      return response.json();
    },
    onSuccess: (data: { isEdit?: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      setIsModalOpen(false);
      setEditingOrg(null);
      resetCreateForm();
      showToast(
        data?.isEdit
          ? 'Organization updated successfully!'
          : 'Organization created successfully. Invitation email with onboarding form has been sent.',
        'success',
      );
    },
    onError: (error: Error) => {
      setFormError(error.message);
      showToast(error.message, 'error');
    },
  });

  const handleOpenCreateModal = () => {
    setEditingOrg(null);
    resetCreateForm();
    setIsModalOpen(true);
  };

  const handleOpenEditModal = async (org: Organization) => {
    setEditingOrg(org);
    setFormError(null);
    setIsModalOpen(true);
    setEditFormLoading(true);
    setOrgName(org.name);
    setOrgType(org.type as 'customer' | 'vendor');

    try {
      const response = await authenticatedFetch(`/api/v1/admin/organizations/${org._id}`);
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result?.data) {
        throw new Error(result?.error || 'Failed to load organization details');
      }

      const detail = result.data;
      setOrgName(detail.name || org.name);
      setOrgType((detail.type || org.type) as 'customer' | 'vendor');
      setChargePerVessel(String(detail.chargePerVessel ?? 0));
      if (detail.chargePerVesselCurrency) {
        setChargePerVesselCurrency(detail.chargePerVesselCurrency);
      }
      setNumberOfVessels(
        detail.numberOfVessels != null && detail.numberOfVessels !== ''
          ? String(detail.numberOfVessels)
          : detail.vesselLimit != null
            ? String(detail.vesselLimit)
            : '',
      );
      const phoneSplit = splitContactPhone(detail.phone || detail.primaryAdmin?.phone || '');
      setOrgPhoneCountryCode(phoneSplit.countryCode);
      setOrgPhone(phoneSplit.localNumber);
      setOrgEmail(detail.primaryAdmin?.email || '');
      setAdminName(
        detail.primaryAdmin?.adminName ||
          `${detail.primaryAdmin?.firstName || ''} ${detail.primaryAdmin?.lastName || ''}`.trim(),
      );
    } catch (error: any) {
      showToast(error.message || 'Could not load full organization details', 'error');
      setChargePerVessel(String((org as any).chargePerVessel || 0));
    } finally {
      setEditFormLoading(false);
    }
  };

  // Columns — NO CHECKBOX COLUMN
  const columns = [
    {
      key: 'name',
      header: 'NAME',
      render: (org: Organization) => (
        <button
          onClick={() => navigate(`/organizations/${org._id}`)}
          className="font-medium text-[hsl(var(--primary))] hover:underline text-left"
        >
          {org.name}
        </button>
      ),
    },
    {
      key: 'type',
      header: 'TYPE',
      render: (org: Organization) => (
        <span
          className={cn(
            'px-4 py-1.5 text-xs font-bold rounded-full ring-1 ring-inset',
            org.type === 'customer'
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 ring-blue-200 dark:ring-blue-800'
              : org.type === 'internal'
                ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 ring-sky-200 dark:ring-sky-800'
                : 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 ring-purple-200 dark:ring-purple-800'
          )}
        >
          {org.type === 'customer'
            ? 'Customer'
            : org.type === 'internal'
              ? 'Euroasiann vendors'
              : 'Vendor'}
        </span>
      ),
    },
    {
      key: 'vendorCategory',
      header: 'VENDOR CLASS',
      render: (org: Organization) => {
        if (org.type !== 'vendor' && org.portalType !== 'vendor') {
          return <span className="text-sm text-[hsl(var(--muted-foreground))]">—</span>;
        }
        const isContracted = org.vendorCategory === 'contracted';
        return (
          <div className="space-y-1">
            <span
              className={cn(
                'px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full',
                isContracted
                  ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/50 ring-1 ring-teal-200 dark:ring-teal-800'
                  : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 ring-1 ring-indigo-200 dark:ring-indigo-800'
              )}
            >
              {org.vendorCategoryLabel || (isContracted ? 'Contracted Vendor' : 'Global Vendor')}
            </span>
            {isContracted && (
              <span className="inline-flex px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-md bg-[#0088cc]/10 text-[#0088cc] border border-[#0088cc]/25">
                Strategic Partner
              </span>
            )}
            {org.invitedByCustomerName && (
              <p className="text-xs text-[hsl(var(--muted-foreground))]">via {org.invitedByCustomerName}</p>
            )}
          </div>
        );
      },
    },
    {
      key: 'portalType',
      header: 'PORTAL',
      render: (org: Organization) => (
        <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
          {org.portalType}
        </span>
      ),
    },
    {
      key: 'isActive',
      header: 'STATUS',
      render: (org: Organization) => (
        <span
          className={cn(
            'px-4 py-1.5 text-xs font-bold rounded-full ring-1 ring-inset',
            org.isActive
              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 ring-emerald-200 dark:ring-emerald-800'
              : 'bg-red-100 text-red-800 dark:bg-red-900/50 ring-red-200 dark:ring-red-800'
          )}
        >
          {org.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      key: 'invitations',
      header: 'INVITATIONS',
      render: (org: Organization) => (
        <button
          type="button"
          onClick={() => setInvitationsOrg(org)}
          className="px-4 py-1.5 text-xs font-medium rounded-lg bg-[#0088cc]/10 text-[#0088cc] border border-[#0088cc]/25 hover:bg-[#0088cc]/20 dark:bg-indigo-900/50 dark:text-indigo-300 transition"
        >
          Manage
        </button>
      ),
    },
    {
      key: 'licenseKey',
      header: 'LICENSE KEY',
      render: (org: Organization) => (
        <span className="font-mono text-xs text-[hsl(var(--muted-foreground))]">
          {org.licenseKey || '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'ACTIONS',
      render: (org: Organization) => (
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={() => handleOpenEditModal(org)}
            className="p-2 rounded-lg hover:bg-[hsl(var(--muted))] transition-colors text-[#0088cc] dark:text-blue-400"
            title="Edit"
          >
            <MdEdit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDelete(org)}
            className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-red-600 dark:text-red-400"
            title="Delete"
          >
            <MdDelete className="w-4 h-4" />
          </button>
        </div>
      ),
      className: 'w-32',
    },
  ];

  return (
    <div className="w-full space-y-6">
      {/* Top Tab Bar */}
      <div className="flex items-center gap-3 border-b border-[hsl(var(--border))] pb-4 mb-2">
        <button
          type="button"
          onClick={() => handleTabChange('organizations')}
          className={cn(
            'flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all',
            activeTab === 'organizations'
              ? 'bg-[#0088cc] text-white shadow-md'
              : 'bg-[hsl(var(--muted))]/30 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/60 hover:text-[hsl(var(--foreground))]'
          )}
        >
          <MdBusiness className="w-4 h-4" />
          <span>Organizations</span>
        </button>
        <button
          type="button"
          onClick={() => handleTabChange('onboarding')}
          className={cn(
            'flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all',
            activeTab === 'onboarding'
              ? 'bg-[#0088cc] text-white shadow-md'
              : 'bg-[hsl(var(--muted))]/30 text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]/60 hover:text-[hsl(var(--foreground))]'
          )}
        >
          <MdAssignment className="w-4 h-4" />
          <span>Onboarding Submissions</span>
        </button>
      </div>

      {activeTab === 'onboarding' ? (
        <OnboardingDataPage />
      ) : (
        <>
          {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-bold text-[hsl(var(--foreground))] mb-2 bg-[#0088cc] hover:bg-[#0077b3] transition-all bg-clip-text text-transparent">
            Organizations
          </h1>
          <p className="text-lg text-[hsl(var(--muted-foreground))] font-medium">
            Manage customer and vendor organizations. Vendors are shown as Global Vendors or Contracted Vendors (Euroasiann strategic partners).
          </p>
        </div>
        <button
          onClick={handleOpenCreateModal}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[hsl(var(--primary))] text-white text-sm font-semibold shadow-md hover:shadow-lg hover:bg-[hsl(var(--primary))]/90 transition-colors"
        >
          <MdAdd className="w-4 h-4" />
          <span>Add New Organization</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="p-6 rounded-2xl bg-[hsl(var(--card))]/80 backdrop-blur-xl border border-[hsl(var(--border))]/50 shadow-lg">
        <div className="space-y-4">
          <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] focus-within:border-[hsl(var(--primary))] focus-within:ring-2 focus-within:ring-[hsl(var(--primary))]/20 transition-all">
            <MdSearch className="w-5 h-5 text-[hsl(var(--muted-foreground))] flex-shrink-0" />
            <input
              type="text"
              placeholder="Search organizations by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={onSearchKeyDown}
              className="flex-1 min-w-0 bg-transparent !border-0 !shadow-none outline-none !ring-0 focus:!border-0 focus:!outline-none focus:!ring-0 focus:!shadow-none text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]"
            />
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 text-[hsl(var(--foreground))] font-semibold">
              <MdFilterList className="w-5 h-5" />
              <span>Filters:</span>
            </div>
            <select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                if (e.target.value === 'customer') {
                  setVendorTypeFilter('all');
                }
              }}
              className="px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] shadow-sm focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-all duration-200 font-medium"
            >
              <option value="all">All Types</option>
              <option value="customer">Customer Only</option>
              <option value="vendor">Vendor Only</option>
            </select>
            {(filterType === 'vendor' || filterType === 'all') && (
              <select
                value={vendorTypeFilter}
                onChange={(e) =>
                  setVendorTypeFilter(e.target.value as 'all' | 'global' | 'contracted')
                }
                className="px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] shadow-sm focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-all duration-200 font-medium"
              >
                <option value="all">All Vendor Classes</option>
                <option value="global">Global Vendors</option>
                <option value="contracted">Contracted Vendors (Strategic Partners)</option>
              </select>
            )}
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] shadow-sm focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-all duration-200 font-medium"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            <ClearFiltersButton
              visible={
                !!searchQuery.trim() ||
                filterActive !== 'all' ||
                filterType !== 'all' ||
                vendorTypeFilter !== 'all'
              }
              onClick={() => {
                clearSearch();
                setFilterActive('all');
                setFilterType('all');
                setVendorTypeFilter('all');
              }}
              variant="toolbar"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <PageSkeleton variant="table" />
      ) : (
        <div className="p-6 rounded-2xl bg-[hsl(var(--card))]/80 backdrop-blur-xl border border-[hsl(var(--border))]/50 shadow-lg">
          <div className="mb-6">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Showing {filteredOrgs.length} organization{filteredOrgs.length !== 1 ? 's' : ''}
            </p>
          </div>

          <DataTable
            columns={columns}
            data={Array.isArray(filteredOrgs) ? filteredOrgs : []}
            emptyMessage="No organizations found."
          />
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingOrg ? 'Edit Organization' : 'Add New Organization'}
      >
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2">
              Organization Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              className={cn(
                'w-full px-4 py-2.5 border-2 rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))]',
                'focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent transition-all',
              )}
              placeholder="Enter organization name"
            />
          </div>

          {editFormLoading ? (
            <PageSkeleton variant="panel" />
          ) : (
            <>
              <div>
                <label className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2">
                  Admin Email {!editingOrg && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="email"
                  value={orgEmail}
                  onChange={(e) => setOrgEmail(e.target.value)}
                  readOnly={!!editingOrg}
                  disabled={!!editingOrg}
                  className={cn(
                    'w-full px-4 py-2.5 border-2 rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))]',
                    'focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent transition-all',
                    editingOrg && 'opacity-70 cursor-not-allowed',
                  )}
                  placeholder="Enter admin email"
                />
                {editingOrg && (
                  <p className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
                    Admin email cannot be changed here. Use user management to update login email.
                  </p>
                )}
              </div>
              <div>
                <label
                  className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2"
                  htmlFor="org-phone"
                >
                  Mobile Number
                </label>
                <div className="flex gap-2 items-stretch">
                  <CountryCodeSelect
                    id="org-phone-country-code"
                    value={orgPhoneCountryCode}
                    onChange={setOrgPhoneCountryCode}
                    className="w-[8.5rem] shrink-0"
                  />
                  <input
                    id="org-phone"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel-national"
                    value={orgPhone}
                    onChange={(e) => setOrgPhone(normalizeContactPhoneLocal(e.target.value))}
                    disabled={editFormLoading}
                    className={cn(
                      'min-w-0 flex-1 px-4 py-2.5 border-2 rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))]',
                      'focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent transition-all',
                      'tabular-nums tracking-normal',
                    )}
                    placeholder="Enter mobile number"
                  />
                </div>
                <p className="mt-1.5 text-xs text-[hsl(var(--muted-foreground))]">
                  Select country code, then enter the local mobile number.
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2">
                  Admin Name {!editingOrg && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="text"
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  disabled={editFormLoading}
                  className={cn(
                    'w-full px-4 py-2.5 border-2 rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))]',
                    'focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent transition-all',
                  )}
                  placeholder="Enter admin name"
                />
              </div>
              {!editingOrg && (
                <p className="text-xs text-[hsl(var(--muted-foreground))] -mt-2">
                  An invitation email with the onboarding form and login credentials will be sent to the admin email.
                </p>
              )}
            </>
          )}

          {!editingOrg && (
            <div>
              <label className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2">
                Organization Type <span className="text-red-500">*</span>
              </label>
              <select
                value={orgType}
                onChange={(e) => setOrgType(e.target.value as any)}
                className={cn(
                  'w-full px-4 py-2.5 border-2 rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))]',
                  'focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent transition-all',
                )}
              >
                <option value="customer">Customer</option>
                <option value="vendor">Vendor</option>
                <option value="internal">Euroasiann vendors</option>
              </select>
            </div>
          )}

          {orgType === 'customer' && !editFormLoading && (
            <div>
              <label className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-2">
                Number of Vessels
              </label>
              <input
                type="number"
                min="0"
                value={numberOfVessels}
                onChange={(e) => setNumberOfVessels(e.target.value)}
                className={cn(
                  'w-full px-4 py-2.5 border-2 rounded-lg bg-[hsl(var(--card))] text-[hsl(var(--foreground))]',
                  'focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-transparent transition-all',
                )}
                placeholder="Enter number of vessels"
              />
            </div>
          )}

          {orgType === 'customer' && !editFormLoading && (
            <CurrencyAmountInput
              key={`${portalCurrency}-${chargePerVesselCurrency}`}
              label="Per Vessel Price"
              value={chargePerVessel}
              onChange={setChargePerVessel}
              currencyCode={chargePerVesselCurrency}
              onCurrencyCodeChange={setChargePerVesselCurrency}
              required={!editingOrg}
              helperText="This amount will be charged for every vessel the customer adds."
            />
          )}

          {formError && (
            <div className="p-3 rounded-lg bg-[hsl(var(--destructive))]/10 border border-red-200 dark:border-red-800 text-[hsl(var(--destructive))] text-sm">
              {formError}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-[hsl(var(--border))]">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              disabled={saveMutation.isPending}
              className="px-4 py-2 rounded-lg bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] font-semibold hover:bg-[#0088cc]/15 dark:hover:bg-[#0088cc]/20 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                setFormError(null);
                saveMutation.mutate();
              }}
              disabled={saveMutation.isPending || editFormLoading}
              className="px-4 py-2 rounded-lg bg-[hsl(var(--primary))] text-white font-semibold hover:bg-[hsl(var(--primary))]/90 transition-colors disabled:opacity-50 shadow-lg hover:shadow-xl"
            >
              {saveMutation.isPending ? 'Saving...' : editingOrg ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </Modal>

      <OrganizationInvitationsModal
        organization={invitationsOrg}
        isOpen={!!invitationsOrg}
        onClose={() => setInvitationsOrg(null)}
        apiBasePath="/api/v1/admin"
      />
        </>
      )}
    </div>
  );
}

